'use client'

import '@/components/editor/style.scss'

import { IPost } from '@/api/interfaces/IPost'
import { PostApi } from '@/api/post'
import { Avatar } from '@/components/Avatar'
import HashTags from '@/components/HashTag'
import { Icon } from '@/components/Icon'
import { IncrementalButton } from '@/components/buttons/IncrementalButton'
import { MoreButton } from '@/components/buttons/MoreButton'
import { useDialogStore } from '@/components/dialog/FullScreenBackDrop'
import { useMutation } from '@/hooks/useMutation'
import { createT } from '@/locales/index'
import { WithT } from '@/locales/types'
import { AuthUser, useAuthStore } from '@/store/auth'
import { formatDate, fromNow } from '@/util/datetime'
import { formatNumber } from '@/util/number'
import classNames from 'classnames'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { CheckOutline } from '@/components/editor/icons'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { PaginatedFanboardPostComment } from '@/components/fanboard/PaginatedFanboardPostComment'
import useSWR, { useSWRConfig } from 'swr'
import { GenericLink } from '@/components/GenericLink'
import { useFanboardState } from '@/store/client'

type Props = {
  post: IPost.IPostRO
  me?: AuthUser
  goBack?: boolean
}

export const CommunityViewClientRender = ({
  post,
  translations,
  me,
}: WithT<Props>) => {
  const t = createT(translations)
  const router = useRouter()
  const authUser = useAuthStore()

  const { state, set } = useFanboardState(post.id, post)

  const onClickLike = async () => {
    const res = await PostApi(authUser.accessToken).like(post.id)
    set({
      isLike: res,
      likeCount: res ? state.likeCount + 1 : state.likeCount - 1,
      isBookmark: state.isBookmark,
    })
  }

  const onClickHashTag = (text: string) => {
    router.push(`/search?q=${text}&search-tab=FANBOARD`)
  }

  const isAuthor = post.writer.username === me?.username

  return (
    <>
      <div
        className="quilljs-content"
        dangerouslySetInnerHTML={{ __html: post.body }}
      />

      {!!post.voteCandidates?.length && (
        <PostVote post={post} isAuthor={isAuthor} />
      )}
      <HashTags tags={post.hashTags} onClick={onClickHashTag} />
      <div className="flex gap-3">
        <button className="flex items-center gap-1.5" onClick={onClickLike}>
          <Icon
            name={state?.isLike ? 'heart-fill' : 'heart-line'}
            size={20}
            className={classNames({
              '!text-gray-600': !state?.isLike,
              '!text-key': state?.isLike,
            })}
          />
          <span className="text-gray-600 font-medium text-body1">
            {t('likes-count', { count: formatNumber(state?.likeCount) })}
          </span>
        </button>
        <button className="flex items-center gap-1.5">
          <Icon name="message-line" size={20} className="!text-gray-600" />
          <span className="text-gray-600 font-medium text-body1">
            {t('write-comment')}
          </span>
        </button>
      </div>
      <hr className="border-gray-200" />

      <PaginatedFanboardPostComment post={post} translations={translations} />
    </>
  )
}

export const PostHeader = ({ post, me, translations }: WithT<Props>) => {
  const { data } = useSWR(['post', post.id], () => PostApi().get(post.id))

  return (
    <div className="flex gap-2 items-center justify-between">
      <Link
        href={`/@${post.writer.username}`}
        className="flex gap-2 items-center"
      >
        <Avatar size={32} image={post.writer} />
        <div className="flex flex-col gap-0.5 flex-1">
          <p className="text-body1">{post.writer.nickname}</p>
          <div className="text-body2 text-gray-700" suppressHydrationWarning>
            {fromNow(post.createDate, translations)}
          </div>
        </div>
      </Link>

      <div className="hidden pc:block">
        {data && (
          <PostActionIcon post={data} me={me} translations={translations} />
        )}
      </div>
    </div>
  )
}

export const PostActionIcon = ({
  post,
  me,
  goBack,
  translations,
}: WithT<Props>) => {
  const { state, set } = useFanboardState(post.id, post)

  const router = useRouter()
  const t = createT(translations)
  const dialog = useDialogStore()
  const mutate = useMutation()
  const { accessToken } = useAuthStore()

  const config = useSWRConfig()

  let menu = []

  if (post.writer.username === me?.username) {
    menu = [
      {
        id: 'EDIT',
        text: t('edit'),
      },
      {
        id: 'DELETE',
        text: t('delete'),
      },
    ]
  } else {
    menu = [
      {
        id: 'REPORT',
        text: t('report'),
      },
    ]
  }

  const onClickMenu = async (id: string) => {
    if (id === 'EDIT') {
      const query = post.user ? `&username=${post.user.username}` : ''
      router.push(`/fanboard/edit?id=${post.id}${query}`)
    }

    if (id === 'DELETE') {
      dialog.alert({
        title: t('dialog-delete-confirm-title'),
        cancelable: true,
        onConfirm: async () => {
          await mutate(PostApi(accessToken).delete(post.id))
          router.refresh()
          dialog.pop()

          if (goBack) {
            router.replace('/fanboard')
            return
          }
        },
      })
    }

    if (id === 'REPORT') {
      dialog.push('report')
    }
  }

  const onClickBookmark = async () => {
    const v = await mutate(PostApi().save(post.id))

    set({ isBookmark: v, isLike: state.isLike, likeCount: state.likeCount })

    config.mutate(
      (k) => Array.isArray(k) && k[0] === 'fanboard-post-paginated-infinite',
    )
  }

  return (
    <div className="flex items-center gap-4">
      <GenericLink onClick={onClickBookmark} privilege>
        <Icon
          name={state.isBookmark ? 'bookmark-fill' : 'bookmark'}
          size={24}
          className={state.isBookmark ? '!text-key' : '!text-gray-500'}
        />
      </GenericLink>

      <MoreButton menu={menu} onClick={onClickMenu} />
    </div>
  )
}

type PostVoteProps = {
  post: IPost.IPostRO
  isAuthor: boolean
}

const PostVote = ({ post, isAuthor }: PostVoteProps) => {
  const { state, set } = useFanboardState(post.id, post)
  const dialog = useDialogStore()

  const vote = state.vote

  const [selected, select] = useState(
    vote?.candidates?.findIndex((c) => c.id === vote?.selectedId) ?? -1,
  )
  const [revote, setRevote] = useState(false)
  const [tick, setTick] = useState(0)

  const candidates = vote?.candidates

  const totalCount = useMemo(() => {
    if (!candidates) {
      return 0
    }

    let c = 0
    for (const candidate of candidates) {
      c += candidate.count ?? 0
    }

    return c
  }, [candidates])

  const onSelect = (candidate: IPost.VoteCandidateRO, index: number) => {
    if (vote?.endDate) {
      return
    }

    if (vote?.selectedId && !revote) {
      return
    }

    if (selected === index) {
      select(-1)
      return
    }

    select(index)
  }

  const onClickVote = useCallback(async () => {
    if (selected === -1) {
      return
    }

    if (vote?.endDate) {
      return
    }

    if (!candidates) {
      return
    }

    await PostApi().vote(post.id, candidates[selected].id)
    const _post = await PostApi().get(post.id)

    set({
      vote: {
        candidates: _post?.voteCandidates!,
        selectedId: _post?.voteSelectedId!,
        endDate: _post?.voteEndDate!,
      },
    })
    setTick((prev) => prev + 1)
    setRevote(false)
  }, [candidates, post.id, selected, set, vote])

  const onClickReVote = useCallback(() => {
    setRevote(true)
    select(-1)
  }, [])

  const onClickCloseVote = useCallback(() => {
    if (!isAuthor) {
      return
    }

    dialog.alert({
      title: '투표를 정말로 종료하시겠습니까?',
      cancelable: true,
      onConfirm: async () => {
        await PostApi().closeVote(post.id)
        const _post = await PostApi().get(post.id)
        set({
          vote: {
            candidates: _post?.voteCandidates!,
            selectedId: _post?.voteSelectedId!,
            endDate: _post?.voteEndDate!,
          },
        })
        dialog.pop()
      },
    })
  }, [dialog, isAuthor, post.id, set])

  const inputLabel = (candidate: IPost.VoteCandidateRO, index: number) => {
    const check = (
      <div className="z-10 text-key">
        <CheckOutline size={16} />
      </div>
    )
    const ratio = (
      <span
        className={[
          'text-sm',
          'z-10',
          vote?.selectedId === candidate.id ? 'text-key' : 'text-gray-700',
        ].join(' ')}
      >
        {((candidate.count / totalCount) * 100).toFixed(0)}%
      </span>
    )

    // 최초 투표 선택 시
    if (!vote?.endDate && !vote?.selectedId && selected === index) {
      return check
    }

    // 다시 투표 선택 시
    if (revote && selected === index) {
      return check
    }

    // 다시 투표 선택 안했을 시
    if (revote) {
      return null
    }

    // 투표 완료
    if (vote?.endDate || vote?.selectedId) {
      return ratio
    }
  }

  const inputBg = useCallback(
    (candidate: IPost.VoteCandidateRO, index: number) => {
      let ratio = (candidate.count / totalCount) * 100

      if (revote || (!vote?.endDate && !vote?.selectedId)) {
        ratio = 100
      }

      let className = 'absolute top-0 left-0 h-full w-full'

      if (selected === index) {
        className += ' bg-key bg-opacity-5'
      } else {
        className += ' bg-black bg-opacity-[0.04]'
      }

      return (
        <div
          className={className}
          style={{
            width: `${ratio}%`,
          }}
        />
      )
    },
    [candidates, selected, totalCount, vote],
  )

  const actions = useCallback(() => {
    if (vote?.endDate) {
      return (
        <span className="text-gray-600 text-sm">
          {formatDate(vote.endDate)} 종료됨
        </span>
      )
    }

    const voted = !!vote?.selectedId && !revote

    return (
      <>
        {isAuthor && (selected === -1 || voted) && (
          <>
            <button type="button" onClick={onClickCloseVote}>
              투표 종료하기
            </button>
            <div className="w-[1px] h-[16px] inline-block bg-gray-300 mx-2" />
          </>
        )}
        {voted ? (
          <button
            type="button"
            className="text-gray-600"
            onClick={onClickReVote}
          >
            투표 다시하기
          </button>
        ) : (
          <button
            type="button"
            className={[selected === -1 ? 'text-gray-600' : 'text-key'].join(
              ' ',
            )}
            onClick={onClickVote}
          >
            투표하기
          </button>
        )}
      </>
    )
  }, [
    isAuthor,
    onClickCloseVote,
    onClickReVote,
    onClickVote,
    vote,
    revote,
    selected,
  ])

  return (
    <div className="border rounded-lg px-2.5 py-3 border-gray-200 mb-4">
      {candidates?.map((candidate, i) => {
        return (
          <div
            key={candidate.id}
            className={`relative overflow-hidden flex items-center border h-10 px-3 rounded text-sm mb-2.5 last-of-type:mb-0 text-gray-700 cursor-pointer ${
              selected === i ? 'border-key' : 'border-gray-200'
            }`}
            onClick={() => onSelect(candidate, i)}
          >
            <div
              className={`whitespace-nowrap overflow-ellipsis overflow-hidden flex-1 z-10 ${
                selected === i ? 'text-key' : 'text-gray-700'
              }`}
            >
              {candidate.name}
            </div>
            {inputLabel(candidate, i)}
            {inputBg(candidate, i)}
          </div>
        )
      })}
      <div className="flex items-center mt-3">
        <span className="text-body3 text-gray-600">{totalCount}명 참여</span>
        <div className="flex items-center ml-auto text-body1 text-gray-600 font-medium">
          {actions()}
        </div>
      </div>
      {/* <BaseDial
    show={modal.show}
    toggle={modal.toggle}
    modalKey="community-vote-close-confirm"
  >
    <p className="font-medium text-center pt-8 pb-10">
      투표를 정말로 종료하시겠습니까?
    </p>
    <ConfirmButton
      onClickConfirm={onConfirmClose}
      onClickCancel={() =>
        modal.toggle(false, 'community-vote-close-confirm')
      }
    />
  </BaseDial> */}
    </div>
  )
}
